// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import { User, Settings, LogOut, ChevronDown, ShieldCheck } from 'lucide-react';
import '../styles/twin-icons-shared.css';
import './UserAccountMenu.css';

export interface ConnectedUserProfile {
  name: string;
  email?: string;
  role: string;
  avatarUrl?: string;
  status: 'online' | 'busy' | 'away';
}

interface UserAccountMenuProps {
  userProfile?: ConnectedUserProfile;
  onOpenSettings: () => void;
  onLogout: () => void;
  activeTimezoneIana?: string;
  activeLocationName?: string;
  activeThemeName?: string;
}

export const DEFAULT_USER_PROFILE: ConnectedUserProfile = {
  name: 'Raúl Nogales',
  email: 'raul.nogales@101obex.com',
  role: 'System Administrator',
  status: 'online',
};

// === COMPONENTE: MENÚ DESPLEGABLE DE CUENTA DE USUARIO ===
export const UserAccountMenu: React.FC<UserAccountMenuProps> = ({
  userProfile = DEFAULT_USER_PROFILE,
  onOpenSettings,
  onLogout,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) setIsOpen(false); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const initials = userProfile.name.split(' ').map((part) => part[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  // === RENDER ===
  return (
    <div className="twin-accountmenu" ref={menuRef}>
      {/* === BOTÓN DISPARADOR === */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`twin-accountmenu-trigger ${isOpen ? 'twin-accountmenu-trigger--open' : ''}`}
        title={`Connected user: ${userProfile.name} - Click to view options`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="twin-accountmenu-avatar-wrap">
          <div className="twin-accountmenu-avatar">{initials || <User className="twin-icon-sm" />}</div>
          <span className="twin-accountmenu-status-dot" title="Status: Connected" />
        </div>

        <div className="twin-accountmenu-identity">
          <span className="twin-accountmenu-name">{userProfile.name}</span>
          <span className="twin-accountmenu-role">{userProfile.role}</span>
        </div>

        <ChevronDown className={`twin-icon-sm twin-accountmenu-chevron ${isOpen ? 'twin-accountmenu-chevron--open' : ''}`} />
      </button>

      {/* === MENÚ DESPLEGABLE === */}
      {isOpen && (
        <div className="twin-accountmenu-dropdown">
          {/* === TARJETA DE INFO DE USUARIO === */}
          <div className="twin-accountmenu-info-card">
            <div className="twin-accountmenu-info-avatar">{initials || <User className="twin-icon-lg" />}</div>
            <div className="twin-accountmenu-info-text">
              <span className="twin-accountmenu-info-name">{userProfile.name}</span>
              <div className="twin-accountmenu-info-role">
                <ShieldCheck className="twin-icon-xs" />
                <span>{userProfile.role}</span>
              </div>
            </div>
          </div>

          <div className="twin-accountmenu-divider" />

          {/* === OPCIONES PRINCIPALES === */}
          <div className="twin-accountmenu-options">
            <button type="button" onClick={() => { setIsOpen(false); onOpenSettings(); }} className="twin-accountmenu-option">
              <div className="twin-accountmenu-option-icon"><Settings className="twin-icon-sm twin-accountmenu-settings-icon" /></div>
              <div className="twin-accountmenu-option-text">
                <div className="twin-accountmenu-option-title">Settings</div>
                <div className="twin-accountmenu-option-hint">Theme, system preferences & account</div>
              </div>
            </button>

            <button type="button" onClick={() => { setIsOpen(false); onLogout(); }} className="twin-accountmenu-option twin-accountmenu-option--danger">
              <div className="twin-accountmenu-option-icon twin-accountmenu-option-icon--danger"><LogOut className="twin-icon-sm" /></div>
              <div className="twin-accountmenu-option-text">
                <div className="twin-accountmenu-option-title twin-accountmenu-option-title--danger">Sign Out</div>
                <div className="twin-accountmenu-option-hint twin-accountmenu-option-hint--danger">End operator session</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};